import React, { useState, useEffect, useRef } from 'react';
import { snapToNearest } from '../utils/snapMagnitude';
import { formatRelativeTime } from '../utils/formatTime';

const USGS_URL =
  'https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/4.5_day.geojson';

async function fetchQuakes() {
  const res = await fetch(USGS_URL);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  return json.features
    .map(f => ({
      id:        f.properties.code,
      magnitude: f.properties.mag,
      place:     f.properties.place,
      lat:       f.geometry.coordinates[1],
      lon:       f.geometry.coordinates[0],
      depth:     f.geometry.coordinates[2],
      time:      new Date(f.properties.time),
      tsunami:   f.properties.tsunami === 1,
    }))
    .sort((a, b) => b.time - a.time);
}

function badgeStyle(mag) {
  if (mag >= 7.0) return { background: '#fcebeb', color: '#a32d2d' };
  if (mag >= 6.0) return { background: '#faeeda', color: '#854f0b' };
  return { background: '#faeeda', color: '#633806' };
}

// btnRef: ref to the trigger button, used to ignore its clicks in the outside-click handler
export default function EarthquakeDropdown({ btnRef, onClose, onUse }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dropRef = useRef(null);

  function load() {
    setLoading(true);
    setError(null);
    fetchQuakes()
      .then(data => { setEvents(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }

  // Fetch on mount (triggered each time this component instance is created)
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close when clicking outside the dropdown and outside the trigger button.
  // Uses 'click' (not 'mousedown') so the hit-test for whatever else was
  // clicked (e.g. a sheet's close button) has already resolved before this
  // unmounts the dropdown — mousedown would reflow the layout mid-gesture
  // and could steal the click from that other element.
  useEffect(() => {
    function onDocClick(e) {
      if (
        dropRef.current && !dropRef.current.contains(e.target) &&
        btnRef.current  && !btnRef.current.contains(e.target)
      ) {
        onClose();
      }
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [onClose, btnRef]);

  return (
    <div className="eq-dropdown" ref={dropRef}>
      {/* Header */}
      <div className="eq-header">
        <span className="eq-header-title">Recent Earthquakes</span>
        <span className="eq-header-sub">USGS · M ≥ 4.5 · last 24 hrs</span>
      </div>

      {/* Loading */}
      {loading && (
        <div className="eq-loading">
          <span className="eq-spinner" />
          Loading…
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="eq-error">
          <p>Could not load earthquake data. Check your connection and try again.</p>
          <button className="eq-retry-btn" onClick={load}>Retry</button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && events.length === 0 && (
        <p className="eq-empty">
          No significant earthquakes (M ≥ 4.5) in this region in the past 24 hours
        </p>
      )}

      {/* Earthquake rows */}
      {!loading && !error && events.map(eq => {
        const snapped  = snapToNearest(eq.magnitude);
        const willSnap = Math.abs(eq.magnitude - snapped) > 1e-9;
        return (
          <div className="eq-row" key={eq.id}>
            <div className="eq-badge" style={badgeStyle(eq.magnitude)}>
              M{eq.magnitude.toFixed(1)}
            </div>

            <div className="eq-info">
              <div className="eq-place">{eq.place}</div>
              <div className="eq-meta">
                Lat {eq.lat.toFixed(2)}&nbsp;&nbsp;Lng {eq.lon.toFixed(2)}
                &nbsp;·&nbsp;Depth {Math.round(eq.depth)} km
                &nbsp;·&nbsp;{formatRelativeTime(eq.time)}
              </div>
              {(eq.tsunami || willSnap) && (
                <div className="eq-tags">
                  {eq.tsunami && (
                    <span className="eq-tsunami-tag">tsunami alert</span>
                  )}
                  {willSnap && (
                    <span className="eq-snap-note">
                      M {eq.magnitude.toFixed(1)} → nearest M {snapped.toFixed(1)}
                    </span>
                  )}
                </div>
              )}
            </div>

            <button
              className="eq-use-btn"
              onClick={() => { onClose(); onUse(eq); }}
            >
              Use
            </button>
          </div>
        );
      })}
    </div>
  );
}
